import { ReactNode } from 'react';
import { ADMIN_RADII, ADMIN_SPACING, ADMIN_TYPE } from './tokens';

interface AdminCardProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Renders without inner padding — caller provides its own layout (e.g. table). */
  noPadding?: boolean;
  /** Larger padding option for content-dense panels. */
  size?: 'default' | 'lg';
  /** Adds an `id` so AdminPageHeader breadcrumb anchors can link to the card. */
  id?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Generic admin surface (card / panel).
 *
 * - 1px gray-200 border, soft shadow, rounded-xl
 * - Optional header row with title / description / right-aligned actions
 * - `noPadding` for tables that want to control their own internal spacing
 */
export function AdminCard({
  title,
  description,
  actions,
  noPadding = false,
  size = 'default',
  id,
  children,
  className = '',
}: AdminCardProps) {
  const hasHeader = title || description || actions;
  const padding = size === 'lg' ? ADMIN_SPACING.cardLg : ADMIN_SPACING.card;

  return (
    <section
      id={id}
      className={`overflow-hidden border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${ADMIN_RADII.card} ${className}`}
    >
      {hasHeader && (
        <div
          className={`flex flex-col gap-3 border-b border-gray-100 sm:flex-row sm:items-center sm:justify-between ${padding}`}
        >
          <div className="min-w-0">
            {title && (
              <h2 className={`${ADMIN_TYPE.cardTitle} text-gray-950`}>
                {title}
              </h2>
            )}
            {description && (
              <p className={`mt-1 ${ADMIN_TYPE.cardSubtitle}`}>{description}</p>
            )}
          </div>
          {actions && (
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      <div className={noPadding ? '' : padding}>{children}</div>
    </section>
  );
}
